import { Fragment, useEffect, useState, useRef } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import axios from 'axios'
import { t } from 'i18next'
import { useRecoilValue } from 'recoil'
import { Container, Button, Row, Col, Card, CardBody, CardFooter } from 'reactstrap'
import Swal from 'sweetalert2'
import moment from 'moment/moment';

import env from '../../env/src_config'
import Breadcrumbs from '../../layout/breadcrumb/Breadcrumb'
import { headersState } from '../../recoil/recoil'
import { axiosError, errorStatus } from '../../helpers/response'
import { isNull, dateFormatForBE } from '../../izUtils'
import { transformer, linker } from '../../helpers/fields'
import Spinner from '../../components/spinner/Spinner'
import { getSingleItem } from '../../helpers/getSingleItem'
import DisplayFields from '../../helpers/displayFields'
import FileUpload from '../../components/fileUpload/FileUpload'
import EventTechnicians from './list/EventTechnicians'

const CreateEvent = () => {
    const Navigate = useNavigate();
    const { eventId, eventMethod } = useParams()

    const headers = useRecoilValue(headersState);
    const [searchParams] = useSearchParams();
    const [inputs, setInputs] = useState(null)
    const [showErrors, setShowErrors] = useState(false)
    const [autoAssign, setAutoAssign] = useState(false);

    const firstRender = useRef(true);

    const lsEventInputs = 'AKODA.eventInputs';
    const lsTechicians = 'AKODA.eventTechnicians';
    const lsEventFiles = 'AKODA.eventFiles';

    useEffect(() => {
        if (eventId === 'create' || (!isNull(eventMethod) && eventMethod === 'update')) {
            let tech = searchParams.get("tech");
            let date = searchParams.get("date");
            let payload = null;
            let dateFrom = null;
            let dateTo = null;

            if (!isNull(date)) {
                const parsedDate = moment(date, 'YYYY-MM-DD', true);
                dateFrom = dateFormatForBE(parsedDate.clone().hour(7).minute(0).second(0).utc().format(), true);
                dateTo = dateFormatForBE(parsedDate.clone().hour(15).minute(0).second(0).utc().format(), true);

                payload = {
                    from: { value: dateFrom },
                    to: { value: dateTo },
                };
            }

            if (!isNull(tech)) {
                let lsTechs = [{
                    id: tech,
                    assigned: [{
                        from: dateFrom ? dateFrom : '',
                        to: dateTo ? dateTo : ''
                    }],
                }];
                localStorage.setItem(lsTechicians, JSON.stringify(lsTechs));
            }

            updateData(false, payload);
        } else {
            getSingleItem(headers, eventId, '/api/calendar/event/', Navigate).then(data => {
                if (!isNull(data)) {
                    data.fields.technicians.value = data.fields.technicians.values.map(val => {
                        let newAssigned = val.assigned.map(event => {
                            event.from = event.event_from
                            event.to = event.event_to
                            return event;
                        })

                        return { id: val.id, assigned: newAssigned }
                    })
                    setInputs(data.fields);
                }
            }).catch(error => {
                axiosError(error, Navigate);
            })
        }

        return () => {
            localStorage.removeItem(lsEventInputs)
            localStorage.removeItem(lsTechicians)
            localStorage.removeItem(lsEventFiles)
        }
    }, [eventId, eventMethod]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false;
            return;
        }

        updateData(false, inputs);
    }, [autoAssign]) // eslint-disable-line react-hooks/exhaustive-deps

    const updateData = (save, passedInputs) => {
        let payload = { save };
        if (!isNull(passedInputs)) {
            const keys = Object.keys(passedInputs);
            payload.data = {};
            for (let i = 0; i < keys.length; i++) {
                if (!isNull(passedInputs[keys[i]].value) && passedInputs[keys[i]].value.length !== 0) {
                    payload.data[keys[i]] = passedInputs[keys[i]].value;
                } else {
                    payload.data[keys[i]] = "";
                }
            }

            // Get data from localStorage
            let selectedTechnicians = JSON.parse(localStorage.getItem(lsTechicians))

            if (!isNull(selectedTechnicians)) {
                if (isNull(payload.data)) payload.data = {};
                payload.data.technicians = selectedTechnicians.map(technician => technician/*.id*/)
            }

            let eventFiles = JSON.parse(localStorage.getItem(lsEventFiles))
            if (!isNull(eventFiles)) {
                if (isNull(payload.data)) payload.data = {};
                payload.data.files = eventFiles.map(file => file.id);
            }
        }

        if (payload?.data?.technicians !== undefined && payload.data.technicians.length > 0) {
            payload.auto_assign = autoAssign;
        }

        let method = 'post' // Create
        if (eventId !== 'create' && (!isNull(eventMethod) && eventMethod === 'update')) method = 'patch' // Update
        axios[method](env.api + '/api/calendar/event/' + eventId, payload, {headers}).then(response => {
            if (save) {
                if (!isNull(response.data.state) && response.data.state === 'success') {
                    Swal.fire({
                        title: t('saved'),
                        text: response.data.message,
                        icon: 'success',
                        confirmButtonColor: 'var(--theme-default)',
                        confirmButtonText: t('ok'),
                    }).then(() => Navigate(-1))
                } else {
                    setShowErrors(true)
                    errorStatus(response.data, t);
                }
            }

            if (!isNull(response.data.data)) {
                let transformedResponse = transformer(response.data.data);
                setInputs(transformedResponse);
                localStorage.setItem(lsEventInputs, JSON.stringify(transformedResponse));
            }
        }).catch(error => {
            axiosError(error, Navigate);
        });
    }

    const textChange = (value, name) => {
        let clonedInputs = {...inputs}

        if (name === 'from') {
            let from = moment.utc(value).local().format();

            if (isNull(clonedInputs['from'].value) || clonedInputs['from'].value.length === 0) {
                from = moment(from).hour(7).minute(0).second(0).utc().format()
                clonedInputs[name].value = dateFormatForBE(from, true);
            } else {
                clonedInputs[name].value = value;
            }

            if (isNull(clonedInputs['to'].value) || clonedInputs['to'].value.length === 0 || !moment(clonedInputs['to'].value).isSame(from, 'day')) {
                let to = moment(from).hour(15).minute(0).second(0).utc().format()
                clonedInputs['to'].value = dateFormatForBE(to, true);
            }
        } else if (name === 'project_number' || name === 'project_name') {
            clonedInputs[name].value = value;
            clonedInputs[name === 'project_number' ? 'project_name' : 'project_number'].value = '';
        } else {
            clonedInputs[name].value = value;
        }

        updateData(false, clonedInputs);
    }

    // Create fields
    const mapLinker = (field) => {
        const inputlinkerFields = {
            field,
            inputs,
            showErrors,
            textHandler: (value, id) => textChange(value, id),
            selectHandler: (value, id) => textChange(value, id),
            dateHandler: (value, id) => textChange(value, id),
            booleanHandler: (value, id) => textChange(value, id),
        }

        if (eventMethod === 'get') {
            if (!isNull(inputs[field])) return <DisplayFields key={'display-'+inputs[field].name} data={inputs[field]} />
        } else {
            return linker(inputlinkerFields);
        }
    }

    return (
        <Fragment>
            <Breadcrumbs parent={t('breadcrumb.schedule')} title={t('breadcrumb.event')} />
            <Container fluid={true}>
                <Row>
                    <Col sm="12">
                        <Card className="ribbon-wrapper">
                            <CardBody>
                                <div className="ribbon ribbon-clip ribbon-primary">{ (eventId === 'create') ? t('schedule.events.create.title') : ( eventMethod === 'update' ? t('schedule.events.update.title') : t('schedule.events.get.title') ) }</div>
                                {isNull(inputs) ?
                                    <Spinner />
                                    :
                                    <Fragment>
                                        <div className="theme-form">
                                            <Row>
                                                <Col sm="12" md="6">
                                                    { ['project_number', 'project_name', 'client', 'facility', 'from', 'to', 'unassigned_technicians'].map(field => mapLinker(field)) }
                                                    <EventTechnicians
                                                        data={inputs.technicians}
                                                        saveToLS={lsTechicians}
                                                        showErrors={showErrors}
                                                        updateData={() => updateData(false, JSON.parse(localStorage.getItem(lsEventInputs)))}
                                                        eventFrom={inputs.from.value}
                                                        eventTo={inputs.to.value}
                                                        autoAssign={autoAssign}
                                                        setAutoAssign={setAutoAssign}
                                                    />
                                                </Col>
                                                <Col sm="12" md="6">
                                                    { ['description', 'material'].map(field => mapLinker(field)) }
                                                    <FileUpload data={inputs.files} saveToLS={lsEventFiles} inputType="createEvent" />
                                                </Col>
                                            </Row>
                                        </div>
                                    </Fragment>
                                }
                            </CardBody>
                            {(eventId === 'create' || (!isNull(eventMethod) && (eventMethod === 'update' || eventMethod === 'draft'))) &&
                                <CardFooter>
                                    <div className='text-end'>
                                        <Button color="primary" className="mt-2 me-2" onClick={() => updateData(true, inputs)}>{t('save')}</Button>
                                    </div>
                                </CardFooter>
                            }
                        </Card>
                    </Col>
                </Row>
                <div className='d-flex flex-wrap justify-content-between mb-4'>
                    <Button color="danger" className="mt-2 me-2" onClick={() => Navigate(-1)}>{t('back')}</Button>
                </div>
            </Container>
        </Fragment>
    )
}

export default CreateEvent